import { useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { CONNECTOR_LABEL, useConnections } from '@/entities/connection';
import { groupByConnection, useDatasets } from '@/features/catalog-browser';
import type { CatalogRow } from '@/shared/api/generated/catalog';
import { DatasetObjectTypeIcon } from '@/shared/ui/DatasetObjectTypeIcon';
import { EmptyState } from '@/shared/ui/EmptyState';
import { LoadingState, UnavailableState } from '@/shared/ui/States';

import { useReachLookup } from '../api/lineage';
import { summaryFor, summaryKey } from '../model/presentation';
import type { LineageSummary, ReachLookup } from '../model/presentation';

/* The design's first two lineage steps: pick a source, then a table in it.
 *
 * **Two steps rather than one long list**, because the question a reader arrives with is "which
 * database", and a flat list of every table grouped by source answered it only by scrolling. The
 * source cards say up front how much of each source has lineage at all, which is the thing that
 * decides whether opening it is worth the click.
 *
 * **Every registered source is a card**, including one nothing has been harvested from yet — shown
 * disabled with that sentence, because a source missing from the list reads as a source the screen
 * does not know about, which is a different and wrong statement.
 *
 * **One reach request for the whole estate**, read once and shared by both steps: the per-dataset
 * counts are a lookup into it, never an `impact`/`provenance` call per row. A regression to the per-row
 * form would render identically and be an N+1, which is why a test counts the requests.
 *
 * **A failed reach read never reads as "no lineage".** Both steps say the graph could not be read,
 * because a request failure rendered as a property of the data is the class this codebase records on
 * three other screens.
 */

interface Catalogue {
  readonly datasets: ReturnType<typeof useDatasets>;
  readonly lookup: ReachLookup;
  readonly groups: ReturnType<typeof groupByConnection>;
  readonly types: ReadonlyMap<string, string>;
  /** Registered sources with no catalogued dataset, in the Connections screen's order. */
  readonly unharvested: readonly { readonly id: string; readonly name: string }[];
}

function useCatalogue(): Catalogue {
  const datasets = useDatasets({ search: '', connectionId: null });
  /* The same cached read every other screen makes, so names and connector families cannot disagree
     with the Connections screen about a source. */
  const connections = useConnections();
  const lookup = useReachLookup();

  const names = useMemo(
    () => new Map((connections.data?.items ?? []).map((each) => [each.id, each.name])),
    [connections.data],
  );
  const types = useMemo(
    () =>
      new Map(
        (connections.data?.items ?? []).map((each) => [
          each.id,
          CONNECTOR_LABEL[each.connector_type],
        ]),
      ),
    [connections.data],
  );
  const groups = useMemo(() => {
    if (!datasets.isSuccess) return [];
    return groupByConnection(datasets.data.items, {
      status: connections.isPending
        ? 'loading'
        : connections.isError
          ? 'unreadable'
          : 'ready',
      names,
    });
  }, [
    datasets.isSuccess,
    datasets.data,
    connections.isPending,
    connections.isError,
    names,
  ]);
  const unharvested = useMemo(() => {
    /* Only once the catalogue page is complete: a source past a truncated page is not "nothing
       harvested", it is "not on this page", and saying the first would be a claim about the source
       the screen has no grounds for. */
    if (!datasets.isSuccess || datasets.data.truncated) return [];
    const listed = new Set(groups.map((group) => group.connectionId));
    return (connections.data?.items ?? [])
      .filter((each) => !listed.has(each.id))
      .map((each) => ({ id: each.id, name: each.name }));
  }, [datasets.isSuccess, datasets.data, groups, connections.data]);

  return { datasets, lookup, groups, types, unharvested };
}

/** Step 1: the sources, as cards that say how much of each has lineage. */
export function LineageSourcePicker({
  onPick,
}: {
  readonly onPick: (connectionId: string) => void;
}): ReactElement {
  const { t } = useTranslation();
  const { datasets, lookup, groups, types, unharvested } = useCatalogue();

  if (datasets.isPending) {
    return <LoadingState rows={5} label={t('lineage.pick.loading')} />;
  }
  if (datasets.isError) {
    return (
      <UnavailableState
        title={t('lineage.pick.unreadable')}
        guidance={datasets.error.message}
      />
    );
  }

  if (groups.length === 0 && unharvested.length === 0) {
    /* The catalogue is empty — not "nothing matched", because no search ran. */
    return (
      <EmptyState
        title={t('lineage.pick.noDatasets')}
        guidance={t('lineage.pick.noDatasetsWhy')}
      />
    );
  }

  return (
    <div className="lq-ln__pick">
      <div className="lq-ln__sources">
        {groups.map((group) => {
          const withLineage = group.rows.filter(
            (row) => summaryFor(row.id, lookup).kind === 'counted',
          ).length;
          return (
            <button
              type="button"
              key={group.connectionId}
              className="lq-ln__source"
              onClick={() => {
                onPick(group.connectionId);
              }}
            >
              <span className="lq-ln__sourceName">
                {group.label.kind === 'named'
                  ? group.label.name
                  : t(`catalog.group.${group.label.kind}`)}
              </span>
              <span className="lq-ln__sourceType">
                {types.get(group.connectionId) ?? ''}
              </span>
              <span className="lq-ln__sourceCount">
                {t('lineage.pick.datasetCount', { count: group.rows.length })}
              </span>
              <span
                className="lq-ln__sourceReach"
                data-has={withLineage > 0 ? 'true' : undefined}
              >
                {lookup.status === 'ready'
                  ? withLineage > 0
                    ? t('lineage.pick.withLineage', { count: withLineage })
                    : t('lineage.pick.noneYet')
                  : t(`lineage.pick.row.${lookup.status}`)}
              </span>
            </button>
          );
        })}
        {unharvested.map((source) => (
          <button type="button" key={source.id} className="lq-ln__source" disabled>
            <span className="lq-ln__sourceName">{source.name}</span>
            <span className="lq-ln__sourceType">{types.get(source.id) ?? ''}</span>
            <span className="lq-ln__sourceCount">
              {t('lineage.pick.nothingHarvested')}
            </span>
          </button>
        ))}
      </div>

      {datasets.data.truncated ? (
        <p className="lq-ln__note">
          {t('lineage.pick.truncated', { count: datasets.data.items.length })}
        </p>
      ) : null}
    </div>
  );
}

/** Step 2: one source's tables, those with lineage first. */
export function LineageTablePicker({
  connectionId,
  onPick,
}: {
  readonly connectionId: string;
  readonly onPick: (datasetId: string) => void;
}): ReactElement {
  const { t } = useTranslation();
  const { datasets, lookup, groups } = useCatalogue();
  const [filter, setFilter] = useState('');

  if (datasets.isPending) {
    return <LoadingState rows={5} label={t('lineage.pick.loading')} />;
  }
  if (datasets.isError) {
    return (
      <UnavailableState
        title={t('lineage.pick.unreadable')}
        guidance={datasets.error.message}
      />
    );
  }

  const group = groups.find((each) => each.connectionId === connectionId);
  const needle = filter.trim().toLowerCase();
  const rows = (group?.rows ?? [])
    .filter(
      (row) =>
        needle === '' ||
        `${row.schema_name}.${row.object_name}`.toLowerCase().includes(needle),
    )
    .map((row) => ({ row, summary: summaryFor(row.id, lookup) }))
    /* Tables with lineage first, as the design orders them: they are the ones a click leads
       somewhere. Stable otherwise, so the catalogue's own order survives within each half. */
    .sort(
      (a, b) =>
        Number(b.summary.kind === 'counted') - Number(a.summary.kind === 'counted'),
    );

  return (
    <section className="lq-ln__tables" aria-labelledby="lineage-tables-heading">
      <div className="lq-ln__tablesHead">
        <h2 id="lineage-tables-heading" className="lq-ln__tablesTitle">
          {group === undefined
            ? t('lineage.pick.tablesUnknown')
            : t('lineage.pick.tablesIn', {
                source:
                  group.label.kind === 'named'
                    ? group.label.name
                    : t(`catalog.group.${group.label.kind}`),
              })}
        </h2>
        {/* Narrows within this source only. The search across every source is the bar on the
            first step; one search control per step, so two answers to one question never sit on
            the same page. */}
        <input
          type="search"
          className="lq-input lq-ln__filter"
          aria-label={t('lineage.pick.filterLabel')}
          placeholder={t('lineage.pick.filterLabel')}
          value={filter}
          onChange={(event) => {
            setFilter(event.target.value);
          }}
        />
      </div>
      {rows.length === 0 ? (
        <p className="lq-ln__none">
          {group === undefined || group.rows.length === 0
            ? t('lineage.pick.noTables')
            : t('lineage.pick.noMatch')}
        </p>
      ) : (
        <ul className="lq-ln__rows">
          {rows.map(({ row, summary }) => (
            <TableRow key={row.id} row={row} summary={summary} onPick={onPick} />
          ))}
        </ul>
      )}
      {datasets.data.truncated ? (
        <p className="lq-ln__note lq-ln__note--inset">
          {t('lineage.pick.truncated', { count: datasets.data.items.length })}
        </p>
      ) : null}
    </section>
  );
}

function TableRow({
  row,
  summary,
  onPick,
}: {
  readonly row: CatalogRow;
  readonly summary: LineageSummary;
  readonly onPick: (datasetId: string) => void;
}): ReactElement {
  const { t } = useTranslation();
  const name = `${row.schema_name}.${row.object_name}`;

  /* Every table is a way in, as in the design. One with no lineage leads to the third step's card
     that says so and offers the dataset itself — which is more useful than a row that does nothing
     when clicked, and still says the same thing before the click. */
  return (
    <li className="lq-ln__rowItem">
      <button
        type="button"
        className="lq-ln__row"
        onClick={() => {
          onPick(row.id);
        }}
      >
        <span className="lq-ln__rowText">
          <span className="lq-ln__rowName">
            <DatasetObjectTypeIcon objectType={row.object_type} />
            {name}
          </span>
          <span className="lq-ln__rowSub">{t(`catalog.state.${row.state}.label`)}</span>
        </span>
        {summary.kind === 'counted' ? (
          <span className="lq-ln__reach">
            <span>
              <strong className="lq-ln__up">{summary.upstream}</strong>{' '}
              {t('lineage.pick.upstream', { count: summary.upstream })}
            </span>
            <span>
              <strong className="lq-ln__down">{summary.downstream}</strong>{' '}
              {t('lineage.pick.downstream', { count: summary.downstream })}
            </span>
          </span>
        ) : (
          <span className="lq-ln__reachNone">{t(summaryKey(summary))}</span>
        )}
        <span className="lq-ln__arrow" aria-hidden="true">
          →
        </span>
      </button>
    </li>
  );
}
